import { useRoute } from 'nuxt/app'
import { RouteLocationNormalizedLoaded } from 'vue-router'
import { useHead } from '#imports'
import { useImage } from '~/composables/useImage'
import { type TPage, TSeoMixin } from '~/types/page-types'
import { useSettingsStore } from '~/store/settings'
import { sanitizeMarkedHeading } from '~/utils/data-transformers'

function renderCanonical (page: TPage, route: RouteLocationNormalizedLoaded) {
  const arPath = route.fullPath.split('/')
  if (page.slugId || page.slug) {
    arPath[arPath.length - 2] = page.slugId || page.slug
  }
  return 'https://avrotros.nl' + arPath.join('/')
}

export const useHeadData = (page: TPage) => {
  const route = useRoute()

  const settingsStore = useSettingsStore()

  const seoData = page?.seo as TSeoMixin

  const meta: Array<Record<string, any>> = [
    {
      name: 'title',
      content: () => {
        const base = seoData?.search_title || page.title || settingsStore.getSeo.title || 'Error'
        return `${base} | AVROTROS`
      }
    },
    {
      name: 'description',
      content: seoData?.search_description || settingsStore.getSeo.description || 'Error'
    },

    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: route.path },
    { name: 'twitter:title', content: sanitizeMarkedHeading(seoData?.twitter_title || page.title || settingsStore.getSeo.title) },
    { name: 'twitter:description', content: seoData?.twitter_description || seoData?.search_description || settingsStore.getSeo.description },
    {
      name: 'twitter:image',
      content: useImage(
        seoData?.twitter_image || page.image?.image,
        'twitter_image.jpg',
        'fit=cover&width=1200&height=675&quality=65&format=jpg'
      )
    },

    // Facebook
    { property: 'og:url', content: route.path },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: sanitizeMarkedHeading(seoData?.og_title || page.title || settingsStore.getSeo.title) },
    { property: 'og:description', content: seoData?.og_description || seoData?.search_description || settingsStore.getSeo.description },
    {
      property: 'og:image',
      content: useImage(
        seoData?.og_image || page.image?.image,
        'og_image.jpg',
        'fit=cover&width=1200&height=675&quality=65&format=jpg'
      )
    }
  ]

  function getAmsterdamISOString (date = new Date()) {
    // 1. Create a formatter to get the parts of the date in Amsterdam's time
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })

    // 2. Format the date into a machine-readable string
    // The 'sv-SE' locale naturally formats to YYYY-MM-DD
    const parts = formatter.formatToParts(date)
    const p = type => parts.find(part => part.type === type).value

    // 3. Manually get the offset (e.g., +01:00)
    // We use a small trick: subtract the UTC time from the localized Amsterdam time
    const offsetFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      timeZoneName: 'shortOffset'
    })
    const offsetPart = offsetFormatter.formatToParts(date).find(p => p.type === 'timeZoneName').value

    // Convert "GMT+1" to "+01:00"
    const offset = offsetPart === 'GMT' ? '+00:00' : offsetPart.replace('GMT', '').replace('+', '+0').replace('-', '-0') + ':00'

    return `${p('year')}-${p('month')}-${p('day')}T${p('hour')}:${p('minute')}:${p('second')}${offset}`
  }

  if (page?.date_created) {
    const publishedDate = new Date(page?.date_created)
    const formattedPublishedDate = publishedDate.toISOString().split('T')[0] // Format date as YYYY-MM-DD

    meta.push({
      name: 'date',
      content: formattedPublishedDate
    },
    {
      name: 'datePublished',
      content: getAmsterdamISOString(publishedDate)
    }, {
      name: 'dcterms.created',
      content: formattedPublishedDate
    },
    {
      name: 'dc.date.issued',
      content: formattedPublishedDate
    })
  }

  if (page?.date_revisioned) {
    const revisionDate = new Date(page?.date_revisioned)
    const formattedRevisionDate = revisionDate.toISOString().split('T')[0] // Format date as YYYY-MM-DD

    meta.push({
      name: 'dateModified',
      content: getAmsterdamISOString(new Date(revisionDate))
    }, {
      name: 'revised',
      content: formattedRevisionDate
    }, {
      name: 'dcterms.modified',
      content: formattedRevisionDate
    }, {
      name: 'dc.date.modified',
      content: formattedRevisionDate
    })
  }

  if (seoData?.robots) {
    meta.push({ name: 'robots', content: seoData.robots + ', nocache, max-snippet:300, max-image-preview:large, max-video-preview:3' })
  }

  const link = [{ rel: 'canonical', href: renderCanonical(page, route) }]

  const updateHead = () => {
    const headData = {
      htmlAttrs: {
        lang: 'nl-NL'
        // @ts-expect-error (theming, data- attrs are supported but not typed)
        // 'data-domain': computed(() => page?.domain?.color?.name)
      },
      link: link.filter(({ href }) => href),
      meta,
      title: () => {
        const base = seoData?.search_title || page.title || settingsStore.getSeo.title || 'Error'
        return `${sanitizeMarkedHeading(base)} | AVROTROS`
      }
    }

    return useHead(headData)
  }

  return updateHead()
}
